import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../services/api";
import {
    User,
    Mail,
    Calendar,
    Lock,
    Bell,
    Camera,
    Check,
    Eye,
    EyeOff,
    Loader2,
    AlertCircle,
    ChevronLeft,
    LogOut,
    Home,
    Settings,
    PawPrint,
    QrCode,
} from "lucide-react";
import "./Configuracion.css";

/* ═══════════════════════════════════════════════════════════════
   COMPONENTES AUXILIARES
   ═══════════════════════════════════════════════════════════════ */

const Toast = ({ message, type, onClose }) => {
    useEffect(() => {
        const t = setTimeout(onClose, 2800);
        return () => clearTimeout(t);
    }, [onClose]);

    return (
        <div className={`sp-toast sp-toast--${type}`}>
            <AlertCircle size={16} />
            {message}
        </div>
    );
};

const Field = ({ label, icon: Icon, value, onChange, type = "text", placeholder, disabled, error }) => (
    <div className="sp-field">
        <label className="sp-field__label">
            {Icon && <Icon size={14} />}
            {label}
        </label>
        <input
            type={type}
            value={value || ""}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            className={`sp-field__input ${error ? "sp-field__input--error" : ""}`}
        />
        {error && <span className="sp-field__error">{error}</span>}
    </div>
);

const PasswordField = ({ label, value, onChange, placeholder, disabled, error }) => {
    const [show, setShow] = useState(false);
    return (
        <div className="sp-field">
            <label className="sp-field__label">
                <Lock size={14} />
                {label}
            </label>
            <div className="sp-password-wrap">
                <input
                    type={show ? "text" : "password"}
                    value={value || ""}
                    onChange={(e) => onChange?.(e.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    className={`sp-field__input ${error ? "sp-field__input--error" : ""}`}
                />
                <button type="button" className="sp-password-toggle" onClick={() => setShow(!show)} tabIndex={-1}>
                    {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
            </div>
            {error && <span className="sp-field__error">{error}</span>}
        </div>
    );
};

const Toggle = ({ label, description, checked, onChange, disabled }) => (
    <div className="sp-toggle">
        <div className="sp-toggle__text">
            <span className="sp-toggle__title">{label}</span>
            {description && <span className="sp-toggle__desc">{description}</span>}
        </div>
        <label className="sp-switch">
            <input type="checkbox" checked={checked} onChange={(e) => onChange?.(e.target.checked)} disabled={disabled} />
            <span className="sp-switch__slider" />
        </label>
    </div>
);

/* ═══════════════════════════════════════════════════════════════
   MODALES
   ═══════════════════════════════════════════════════════════════ */

const LogoutModal = ({ open, onClose, onConfirm }) => {
    if (!open) return null;
    return (
        <div className="sp-modal-backdrop" onClick={onClose}>
            <div className="sp-modal" onClick={(e) => e.stopPropagation()}>
                <div className="sp-modal__icon sp-modal__icon--danger">
                    <LogOut size={28} />
                </div>
                <h3 className="sp-modal__title">Cerrar sesion?</h3>
                <p className="sp-modal__text">Vas a tener que volver a iniciar sesion para acceder a tu cuenta.</p>
                <div className="sp-modal__actions">
                    <button className="sp-btn sp-btn--light" onClick={onClose}>Cancelar</button>
                    <button className="sp-btn sp-btn--danger" onClick={onConfirm}>Si, salir</button>
                </div>
            </div>
        </div>
    );
};

const DeleteModal = ({ open, onClose, onConfirm }) => {
    const [confirmText, setConfirmText] = useState("");
    if (!open) return null;
    const canDelete = confirmText.trim() === "ELIMINAR";
    return (
        <div className="sp-modal-backdrop" onClick={onClose}>
            <div className="sp-modal" onClick={(e) => e.stopPropagation()}>
                <div className="sp-modal__icon sp-modal__icon--danger">
                    <AlertCircle size={28} />
                </div>
                <h3 className="sp-modal__title">Eliminar cuenta?</h3>
                <p className="sp-modal__text">Esta accion no se puede deshacer. Se borraran todos tus datos.</p>
                <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder='Escribi "ELIMINAR"'
                    className="sp-field__input sp-field__input--center"
                    style={{ marginBottom: "1rem", textTransform: "uppercase", letterSpacing: "0.15em", fontWeight: 700 }}
                />
                <div className="sp-modal__actions">
                    <button className="sp-btn sp-btn--light" onClick={onClose}>Cancelar</button>
                    <button className="sp-btn sp-btn--danger" onClick={onConfirm} disabled={!canDelete}>
                        Eliminar
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════════
   PAGINA PRINCIPAL
   ═══════════════════════════════════════════════════════════════ */

export default function Configuracion() {
    const { user, logout, setUser } = useAuth();
    const navigate = useNavigate();
    const fileRef = useRef(null);

    /* ── Estados ── */
    const [form, setForm] = useState({
        nombre: "",
        apellido: "",
        email: "",
        confirmEmail: "",
        fecha_nacimiento: "",
        recibir_emails: true,
    });
    const [original, setOriginal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [toast, setToast] = useState(null);
    const [errors, setErrors] = useState({});
    const [fotoPerfil, setFotoPerfil] = useState(null);

    const [pwd, setPwd] = useState({ current: "", new: "", confirm: "" });
    const [pwdSaving, setPwdSaving] = useState(false);
    const [pwdErrors, setPwdErrors] = useState({});

    const [showLogout, setShowLogout] = useState(false);
    const [showDelete, setShowDelete] = useState(false);

    /* ── Helper: construir URL de foto ── */
    const buildPhotoUrl = useCallback((path) => {
        if (!path) return null;
        if (path.startsWith("http")) return path;
        const base = process.env.REACT_APP_API_URL || "http://localhost/api-smartpet";
        // Remove trailing slash from base and leading slash from path
        const cleanBase = base.replace(/\/$/, "");
        const cleanPath = path.replace(/^\//, "");
        return `${cleanBase}/${cleanPath}`;
    }, []);

    /* ── Cargar datos ── */
    useEffect(() => {
        if (!user?.id) return;
        let cancelled = false;

        api
            .getUser(user.id)
            .then((res) => {
                if (cancelled) return;
                const u = res.data?.user || res.data || {};


                const payload = {
                    nombre: u.nombre || "",
                    apellido: u.apellido || "",
                    email: u.email || "",
                    confirmEmail: u.email || "",
                    fecha_nacimiento: u.fecha_nacimiento || "",
                    recibir_emails: !!u.recibir_emails,
                };
                setForm(payload);
                setOriginal(payload);

                // Guardar foto por separado - puede venir de la API o del contexto
                const fotoFromApi = u.foto_perfil || u.fotoPerfil || u.photo || u.avatar || u.imagen;
                const fotoFromContext = user?.foto_perfil || user?.fotoPerfil;
                const finalFoto = fotoFromApi || fotoFromContext || null;

                setFotoPerfil(finalFoto);

                setLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error("[Config] Error fetching user:", err);
                showToast("Error al cargar datos", "error");
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [user?.id, buildPhotoUrl]);

    const showToast = useCallback((message, type = "info") => {
        setToast({ message, type });
    }, []);

    const updateField = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
    };

    /* ── Validar formulario ── */
    const validate = () => {
        const e = {};
        if (!form.nombre.trim()) e.nombre = "El nombre es obligatorio";
        if (!form.apellido.trim()) e.apellido = "El apellido es obligatorio";
        if (!form.email.trim()) e.email = "El email es obligatorio";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Email invalido";
        if (form.email !== form.confirmEmail) e.confirmEmail = "Los emails no coinciden";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    /* ── Guardar perfil ── */
    const handleSaveProfile = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        if (!user?.id) return;

        setSaving(true);
        try {
            const payload = {
                nombre: form.nombre.trim(),
                apellido: form.apellido.trim(),
                email: form.email.trim(),
                fecha_nacimiento: form.fecha_nacimiento,
                recibir_emails: form.recibir_emails ? 1 : 0,
            };
            const res = await api.updateUser(user.id, payload);
            if (res.data?.success) {
                const updated = { ...user, ...payload };
                localStorage.setItem("user", JSON.stringify(updated));
                if (setUser) setUser(updated);
                setOriginal({ ...form });
                showToast("Perfil actualizado", "success");
            } else {
                throw new Error(res.data?.error || "Error al guardar");
            }
        } catch (err) {
            showToast(err.message || "Error al guardar", "error");
        } finally {
            setSaving(false);
        }
    };

    /* ── Subir foto ── */
    const handlePhoto = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !user?.id) return;

        setUploading(true);

        try {
            const res = await api.uploadPerfil(user.id, file);
            console.log("[Config] Upload response:", res.data);

            if (res.data?.success) {
                const newPhoto = res.data.foto_perfil || res.data.url || res.data.photo_url;
                console.log("[Config] New photo path:", newPhoto);

                setFotoPerfil(newPhoto);

                const updated = { ...user, foto_perfil: newPhoto };
                localStorage.setItem("user", JSON.stringify(updated));
                if (setUser) setUser(updated);

                showToast("Foto actualizada", "success");
            } else {
                throw new Error(res.data?.error || "Error al subir foto");
            }
        } catch (err) {
            console.error("[Config] Upload error:", err);
            showToast(err.message || "Error al subir foto", "error");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    /* ── Cambiar contrasena ── */
    const validatePwd = () => {
        const e = {};
        if (!pwd.current) e.current = "Ingresa tu contrasena actual";
        if (!pwd.new) e.new = "Ingresa la nueva contrasena";
        else if (pwd.new.length < 6) e.new = "Minimo 6 caracteres";
        if (pwd.new !== pwd.confirm) e.confirm = "Las contrasenas no coinciden";
        setPwdErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (!validatePwd() || !user?.id) return;

        setPwdSaving(true);
        try {
            const res = await api.changePassword(user.id, pwd.current, pwd.new);
            if (res.data?.success) {
                setPwd({ current: "", new: "", confirm: "" });
                setPwdErrors({});
                showToast("Contrasena actualizada", "success");
            } else {
                throw new Error(res.data?.error || "Error al cambiar contrasena");
            }
        } catch (err) {
            showToast(err.message || "Error al cambiar contrasena", "error");
        } finally {
            setPwdSaving(false);
        }
    };

    /* ── Logout ── */
    const handleLogout = () => {
        setShowLogout(false);
        logout();
        navigate("/");
    };

    /* ── Helpers ── */
    const getPhotoUrl = () => {
        return buildPhotoUrl(fotoPerfil);
    };

    const hasChanges = original && (
        form.nombre !== original.nombre ||
        form.apellido !== original.apellido ||
        form.email !== original.email ||
        form.confirmEmail !== original.confirmEmail ||
        form.fecha_nacimiento !== original.fecha_nacimiento ||
        form.recibir_emails !== original.recibir_emails
    );

    /* ── Render ── */
    return (
        <div className="sp-config">
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}
            <LogoutModal open={showLogout} onClose={() => setShowLogout(false)} onConfirm={handleLogout} />
            <DeleteModal open={showDelete} onClose={() => setShowDelete(false)} onConfirm={() => { setShowDelete(false); showToast("Cuenta eliminada", "success"); setTimeout(() => { logout(); navigate("/"); }, 1200); }} />

            {/* Header */}
            <header className="sp-config__header">
                <button className="sp-config__back" onClick={() => navigate(-1)} aria-label="Volver">
                    <ChevronLeft size={22} />
                </button>
                <h1>Configuracion</h1>
                <div className="sp-config__spacer" />
            </header>

            <main className="sp-config__main">
                {/* Hero */}
                <section className="sp-hero">
                    <div>
                        <span className="sp-hero__kicker">Mi cuenta</span>
                        <h2 className="sp-hero__title">Configuracion de perfil</h2>
                        <p className="sp-hero__text">Visualiza y actualiza tus datos personales, foto y preferencias.</p>
                    </div>
                    <div className="sp-hero__badge">
                        <Check size={14} />
                        Perfil activo
                    </div>
                </section>

                {/* Grid */}
                <div className="sp-config__grid">
                    {/* Sidebar - Perfil */}
                    <aside className="sp-profile">
                        <div className="sp-profile__photo-wrap">
                            <button
                                type="button"
                                className="sp-profile__photo"
                                onClick={() => fileRef.current?.click()}
                                disabled={uploading}
                                aria-label="Cambiar foto de perfil"
                            >
                                {getPhotoUrl() ? (
                                    <img src={getPhotoUrl()} alt="Foto de perfil" onError={(e) => { console.log("[Config] Image failed to load:", getPhotoUrl()); e.target.style.display = "none"; }} />
                                ) : (
                                    <User size={48} />
                                )}
                                <span className="sp-profile__overlay">
                                    <Camera size={20} />
                                    <span>Cambiar</span>
                                </span>
                                {uploading && (
                                    <span className="sp-profile__loading">
                                        <Loader2 size={24} className="sp-spin" />
                                    </span>
                                )}
                            </button>
                            <input ref={fileRef} type="file" accept="image/*" hidden onChange={handlePhoto} />
                        </div>

                        <h2 className="sp-profile__name">
                            {form.nombre} {form.apellido}
                        </h2>
                        <p className="sp-profile__email">{form.email}</p>

                        <div className="sp-profile__meta">
                            <div>
                                <span>Fecha nacimiento</span>
                                <strong>{form.fecha_nacimiento || "-"}</strong>
                            </div>
                            <div>
                                <span>Notificaciones</span>
                                <strong>{form.recibir_emails ? "Activadas" : "Desactivadas"}</strong>
                            </div>
                        </div>

                        <button
                            type="button"
                            className="sp-btn sp-btn--secondary sp-btn--full"
                            onClick={() => fileRef.current?.click()}
                            disabled={uploading}
                        >
                            <Camera size={16} />
                            {uploading ? "Subiendo..." : "Subir nueva foto"}
                        </button>

                        <nav className="sp-profile__nav">
                            <button onClick={() => navigate("/")}>
                                <Home size={16} /> Inicio
                            </button>
                            <button onClick={() => navigate(`/misMascotas/${user?.id}`)}>
                                <PawPrint size={16} /> Mis mascotas
                            </button>
                            <button onClick={() => navigate(`/consultas/${user?.id}`)}>
                                <QrCode size={16} /> Chapitas
                            </button>
                            <button onClick={() => setShowLogout(true)} className="sp-profile__nav--logout">
                                <LogOut size={16} /> Cerrar sesion
                            </button>
                        </nav>
                    </aside>

                    {/* Formularios */}
                    <div className="sp-forms">
                        {/* Datos personales */}
                        <section className="sp-card">
                            <div className="sp-card__header">
                                <Settings size={20} />
                                <div>
                                    <h3>Datos personales</h3>
                                    <p>Estos datos se usan para identificar tu cuenta.</p>
                                </div>
                            </div>

                            <form onSubmit={handleSaveProfile} className="sp-form">
                                <div className="sp-form__row">
                                    <Field
                                        label="Nombre"
                                        icon={User}
                                        value={form.nombre}
                                        onChange={(v) => updateField("nombre", v)}
                                        placeholder="Tu nombre"
                                        error={errors.nombre}
                                        disabled={loading}
                                    />
                                    <Field
                                        label="Apellido"
                                        icon={User}
                                        value={form.apellido}
                                        onChange={(v) => updateField("apellido", v)}
                                        placeholder="Tu apellido"
                                        error={errors.apellido}
                                        disabled={loading}
                                    />
                                </div>

                                <Field
                                    label="Fecha de nacimiento"
                                    icon={Calendar}
                                    type="date"
                                    value={form.fecha_nacimiento}
                                    onChange={(v) => updateField("fecha_nacimiento", v)}
                                    disabled={loading}
                                />

                                <div className="sp-form__row">
                                    <Field
                                        label="Correo electronico"
                                        icon={Mail}
                                        type="email"
                                        value={form.email}
                                        onChange={(v) => updateField("email", v)}
                                        placeholder="ejemplo@correo.com"
                                        error={errors.email}
                                        disabled={loading}
                                    />
                                    <Field
                                        label="Confirmar correo"
                                        icon={Mail}
                                        type="email"
                                        value={form.confirmEmail}
                                        onChange={(v) => updateField("confirmEmail", v)}
                                        placeholder="Repeti tu correo"
                                        error={errors.confirmEmail}
                                        disabled={loading}
                                    />
                                </div>

                                <Toggle
                                    label="Recibir notificaciones por email"
                                    description="Te avisaremos sobre novedades importantes de tu cuenta."
                                    checked={form.recibir_emails}
                                    onChange={(v) => updateField("recibir_emails", v)}
                                    disabled={loading}
                                />

                                <div className="sp-form__actions">
                                    <button
                                        type="button"
                                        className="sp-btn sp-btn--light"
                                        onClick={() => {
                                            if (original) setForm({ ...original });
                                            setErrors({});
                                        }}
                                        disabled={!hasChanges || saving}
                                    >
                                        Descartar
                                    </button>
                                    <button
                                        type="submit"
                                        className="sp-btn sp-btn--primary"
                                        disabled={!hasChanges || saving}
                                    >
                                        {saving ? <Loader2 size={16} className="sp-spin" /> : <Check size={16} />}
                                        {saving ? "Guardando..." : "Guardar cambios"}
                                    </button>
                                </div>
                            </form>
                        </section>

                        {/* Seguridad */}
                        <section className="sp-card">
                            <div className="sp-card__header">
                                <Lock size={20} />
                                <div>
                                    <h3>Seguridad</h3>
                                    <p>Cambia tu contrasena periodicamente para proteger tu cuenta.</p>
                                </div>
                            </div>

                            <form onSubmit={handleChangePassword} className="sp-form">
                                <PasswordField
                                    label="Contrasena actual"
                                    value={pwd.current}
                                    onChange={(v) => setPwd((p) => ({ ...p, current: v }))}
                                    placeholder="Ingresa tu contrasena actual"
                                    error={pwdErrors.current}
                                />
                                <div className="sp-form__row">
                                    <PasswordField
                                        label="Nueva contrasena"
                                        value={pwd.new}
                                        onChange={(v) => setPwd((p) => ({ ...p, new: v }))}
                                        placeholder="Minimo 6 caracteres"
                                        error={pwdErrors.new}
                                    />
                                    <PasswordField
                                        label="Confirmar contrasena"
                                        value={pwd.confirm}
                                        onChange={(v) => setPwd((p) => ({ ...p, confirm: v }))}
                                        placeholder="Repeti la nueva contrasena"
                                        error={pwdErrors.confirm}
                                    />
                                </div>
                                <div className="sp-form__actions sp-form__actions--end">
                                    <button
                                        type="submit"
                                        className="sp-btn sp-btn--primary"
                                        disabled={pwdSaving || !pwd.current || !pwd.new || !pwd.confirm}
                                    >
                                        {pwdSaving ? <Loader2 size={16} className="sp-spin" /> : <Check size={16} />}
                                        {pwdSaving ? "Actualizando..." : "Actualizar contrasena"}
                                    </button>
                                </div>
                            </form>
                        </section>

                        {/* Zona peligrosa */}
                        <section className="sp-card sp-card--danger">
                            <div className="sp-card__header">
                                <AlertCircle size={20} />
                                <div>
                                    <h3>Zona peligrosa</h3>
                                    <p>Acciones irreversibles para tu cuenta.</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="sp-btn sp-btn--danger sp-btn--full"
                                onClick={() => setShowDelete(true)}
                            >
                                <LogOut size={16} />
                                Eliminar mi cuenta
                            </button>
                        </section>
                    </div>
                </div>
            </main>
        </div>
    );
}